/**
 * Opportunity products actions — per-product quote steps the user runs.
 */
/**
 * Opportunity products actions — per-product quote steps the user runs.
 */
import React, { useEffect, useState } from 'react';
import { Cube, Lock, WarningCircle } from '@phosphor-icons/react';
import { usePreContractProductsSetup } from './PreContractProductsSetupContext';
import { PreContractProductGenerateProgress } from './PreContractProductGenerateProgress';
import type { PreContractProductCombinationsSectionProps } from './PreContractProductCombinationsSection.types';
import type { PreContractProduct } from '../lib/preContractTypes';
import {
    applyFinancePartnerSyncToGenerateSteps,
    applyLinkedQuoteToGenerateSteps,
    reconcileGenerateStepsWithPlan,
    buildOpportunityGenerateSteps,
} from '../lib/preContractProductGeneratePipeline';
import { loadFinancePartnerSyncStatuses } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/finance';
import { useSystemDesignMissingItems } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/useSystemDesignMissingItems';
import { applyPartnerSyncGatesToGenerateSteps } from '../lib/preContractPartnerSyncGate';
import OBDQuoteDetailsModal from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/OBDQuoteDetailsModal';
import { useOneButtonStore } from '../../store/oneButtonStore';

const GENERATE_PRODUCTS = new Set<PreContractProduct>(['Solar', 'Roof', 'Battery']);

function displayProductName(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    if (product === 'RR') return 'Remove & reinstall';
    return product;
}

export const PreContractProductCombinationsSection: React.FC<
    PreContractProductCombinationsSectionProps
> = ({
    products,
    onRunGenerateStep,
    generateDisabled = false,
    generateDisabledReason,
    generateRunning = false,
    activeGenerateProduct = null,
    generateSteps = [],
    productQuotePlans = {},
    productQuoteLocked = {},
    solarDesignChanges = [],
    utilityChanges = [],
    linkedQuotes = {},
    onOpenLinkedQuote,
    onUnlinkQuote,
}) => {
    const {
        openProductsSetup,
        blocked,
        productsEditLocked,
        productsEditLockedReason,
        loading,
        statusError,
    } = usePreContractProductsSetup();
    const opportunityId = useOneButtonStore((state) => state.opportunityID);
    const salesforceId = useOneButtonStore((state) => state.salesForceId);
    const opportunityDetails = useOneButtonStore((state) => state.opportunityDetails);
    const financePartnerRawSyncStatuses = useOneButtonStore(
        (state) => state.financePartnerRawSyncStatuses,
    );
    const financeSelectionRefreshTrigger = useOneButtonStore(
        (state) => state.financeSelectionRefreshTrigger,
    );
    const systemDesignMissingItems = useSystemDesignMissingItems();
    const [quoteDetails, setQuoteDetails] = useState<{
        quoteId: string;
        partner: string;
        product: string;
    } | null>(null);

    const generateProducts = products.filter((product) => GENERATE_PRODUCTS.has(product));
    const partnerFingerprint = generateProducts
        .map((product) => `${product}:${productQuotePlans[product]?.partner ?? ''}`)
        .join('|');

    useEffect(() => {
        const id = String(opportunityId ?? '').trim();
        if (!id || id === '0') return;
        let cancelled = false;
        void loadFinancePartnerSyncStatuses(id)
            .then((list) => {
                if (cancelled || !Array.isArray(list)) return;
                useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(list);
            })
            .catch(() => {
                /* preview still uses saved finalize / utility / partner flags */
            });
        return () => {
            cancelled = true;
        };
    }, [financeSelectionRefreshTrigger, opportunityId, partnerFingerprint]);
    const configureLocked = blocked || productsEditLocked || loading;
    const stepsDisabled =
        generateRunning || blocked || loading || Boolean(statusError);

    return (
        <>
        <section
            className={`pci-unified-deal pci-unified-products-setup${
                blocked ? ' pci-unified-products-setup--blocked' : ''
            }`}
            aria-label="Opportunity products"
        >
            <header className="pci-unified-deal__head pci-unified-products-setup__head">
                <div>
                    <h2 className="pci-unified-deal__title">Opportunity products</h2>
                    <p className="pci-unified-deal__subtitle">
                        {productsEditLocked
                            ? 'Finalize the oneDRAW design and save utility details before selecting a finance partner'
                            : 'Run each required quote step in order: design, utility, partner, then quote'}
                    </p>
                    {loading ? (
                        <p className="pci-unified-products-setup__status" role="status">
                            Loading opportunity products…
                        </p>
                    ) : statusError ? (
                        <p
                            className="pci-unified-products-setup__status pci-unified-products-setup__status--error"
                            role="alert"
                        >
                            <WarningCircle size={14} weight="fill" aria-hidden />
                            {statusError}
                        </p>
                    ) : null}
                </div>

                <div className="pci-products-setup__head-actions">
                    <button
                        type="button"
                        className="pci-products-setup__open-btn"
                        disabled={configureLocked}
                        title={
                            productsEditLocked
                                ? productsEditLockedReason ?? undefined
                                : blocked
                                  ? statusError ?? undefined
                                  : 'Open full products setup'
                        }
                        onClick={() => openProductsSetup()}
                    >
                        <Cube size={16} weight="duotone" aria-hidden />
                        Configure &amp; save
                    </button>
                </div>
            </header>

            {generateProducts.length > 0 ? (
                <div className="pci-unified-products-setup__body">
                    <div className="pci-products-setup__generate-row" role="group" aria-label="Quote steps by product">
                        {generateProducts.map((product) => {
                            const isActive = activeGenerateProduct === product;
                            const plan = productQuotePlans[product];
                            const previewSteps = plan
                                ? applyPartnerSyncGatesToGenerateSteps(
                                      applyFinancePartnerSyncToGenerateSteps(
                                          buildOpportunityGenerateSteps(product, plan.partner, {
                                              needsFinalize: plan.needsFinalize,
                                              needsUtilityDetails: plan.needsUtilityDetails,
                                              needsPartnerSync: plan.needsPartnerSync,
                                          }),
                                          product,
                                          financePartnerRawSyncStatuses,
                                      ),
                                      product,
                                      plan.partner,
                                      systemDesignMissingItems,
                                      opportunityDetails,
                                  )
                                : [];
                            const liveSteps = applyPartnerSyncGatesToGenerateSteps(
                                applyLinkedQuoteToGenerateSteps(
                                    reconcileGenerateStepsWithPlan(
                                        isActive && generateSteps.length > 0
                                            ? generateSteps
                                            : previewSteps,
                                        previewSteps,
                                        isActive && generateRunning,
                                    ),
                                    linkedQuotes[product],
                                ),
                                product,
                                plan?.partner ?? '',
                                systemDesignMissingItems,
                                opportunityDetails,
                            );
                            const lockedReason = productQuoteLocked[product]?.trim() || null;

                            return (
                                <div key={product} className="pci-products-setup__generate-item">
                                    <div className="pci-products-setup__quote-head">
                                        <span className="pci-products-setup__quote-head-kicker">
                                            Product
                                        </span>
                                        <strong>{displayProductName(product)}</strong>
                                        <span className="pci-products-setup__quote-head-kicker">
                                            Partner
                                        </span>
                                        <span className="pci-products-setup__quote-head-partner">
                                            {plan?.partner?.trim() || 'Select a partner'}
                                        </span>
                                    </div>
                                    {lockedReason ? (
                                        <p
                                            className="pci-products-setup__quote-locked"
                                            role="status"
                                        >
                                            <Lock size={14} weight="bold" aria-hidden />
                                            <span>{lockedReason}</span>
                                        </p>
                                    ) : plan ? (
                                        <PreContractProductGenerateProgress
                                            product={product}
                                            partnerName={plan.partner}
                                            steps={liveSteps}
                                            running={isActive && generateRunning}
                                            preview={!generateRunning || !isActive}
                                            stepDisabled={
                                                stepsDisabled ||
                                                (generateRunning &&
                                                    activeGenerateProduct !== product)
                                            }
                                            stepDisabledReason={
                                                generateRunning &&
                                                activeGenerateProduct !== product
                                                    ? 'Another step is running'
                                                    : generateDisabledReason
                                            }
                                            onStepClick={
                                                onRunGenerateStep
                                                    ? (stepId) =>
                                                          onRunGenerateStep(product, stepId)
                                                    : undefined
                                            }
                                            onOpenLinkedQuote={(quoteId) =>
                                                setQuoteDetails({
                                                    quoteId,
                                                    partner: plan.partner,
                                                    product: displayProductName(product),
                                                })
                                            }
                                            onUnlinkQuote={
                                                onUnlinkQuote
                                                    ? () => onUnlinkQuote(product)
                                                    : undefined
                                            }
                                            designChanges={
                                                product === 'Solar' ? solarDesignChanges : undefined
                                            }
                                            utilityChanges={
                                                product === 'Solar' || product === 'Battery'
                                                    ? utilityChanges
                                                    : undefined
                                            }
                                        />
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </section>
        {quoteDetails ? (
            <OBDQuoteDetailsModal
                quoteId={quoteDetails.quoteId}
                partner={quoteDetails.partner}
                product={quoteDetails.product}
                monthly={0}
                apr={0}
                mode="view"
                onClose={() => setQuoteDetails(null)}
                opportunityId={opportunityId}
                salesforceId={salesforceId}
                opportunityDetails={opportunityDetails}
            />
        ) : null}
        </>
    );
};

export default PreContractProductCombinationsSection;
